/**
 * Pure helpers for converting between ADF (ProseMirror) docs and markdown
 * source. Used by `MarkdownSourceView` to populate its textarea on mount and
 * to round-trip the user's edits back to ADF on unmount.
 *
 * Both functions are intentionally side-effect free and React-free so they can
 * be unit-tested in isolation. Errors from the markdown parser are *thrown* —
 * `markdownToAdf` is a "throws on parse error" boundary; the calling component
 * is responsible for catching and surfacing them.
 *
 * Ticket: https://hello.jira.atlassian.cloud/browse/EDITOR-6558
 */
import { MarkdownTransformer } from '@atlaskit/editor-markdown-transformer';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import { fromProseMirror } from '@atlassian/mdast-util-from-pm';

/**
 * Convert a live ProseMirror document (or its ADF JSON representation) to a
 * markdown string. The supplied schema is used to hydrate JSON inputs and to
 * resolve node-type lookups inside the from-pm handlers.
 */
export const adfToMarkdown = (doc: PMNode, schema: Schema): string => {
	return fromProseMirror(schema)(doc);
};

/**
 * Parse a markdown string back into a ProseMirror document using the supplied
 * schema. Throws if the markdown contains constructs the schema cannot
 * represent — callers must wrap this in try/catch and decide what to do
 * (e.g. discard the change, surface a flag, etc.).
 */
export const markdownToAdf = (markdown: string, schema: Schema): PMNode => {
	const transformer = new MarkdownTransformer(schema);
	return transformer.parse(markdown);
};
